/**
 * Pure selectors over `fim-fusion-run` history. Kept free of React/SWR so the
 * run-splitting logic can be unit-tested directly (see `fusionRuns.test.ts`).
 */
import type { FimFusionRunEntry, FusionRunData } from './types'

// `mode`/`agent_edges` ride along in counts as strings; the UI renders them as
// dedicated badges, not in the counts list.
const RUN_DESCRIPTOR_KEYS = new Set(['mode', 'agent_edges'])

type CountNode = number | string | { [key: string]: CountNode }

function flattenCountNode(
  prefix: string,
  value: CountNode
): [string, string | number][] {
  if (typeof value === 'number' || typeof value === 'string') {
    return [[prefix, value]]
  }
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return Object.entries(value).flatMap(([key, nested]) =>
      flattenCountNode(prefix ? `${prefix}.${key}` : key, nested)
    )
  }
  return [[prefix, String(value)]]
}

/**
 * Counts as renderable `[label, scalar]` pairs. fim-service writes nested count
 * groups (e.g. `ref_hygiene: {split, folded, …}` and deeper matching/link-tier
 * maps) — flatten all levels to dotted labels so render sites never receive an
 * object as a React child.
 */
export function flattenRunCounts(
  counts: FusionRunData['counts'] | undefined
): [string, string | number][] {
  return Object.entries(counts ?? {})
    .filter(([k]) => !RUN_DESCRIPTOR_KEYS.has(k))
    .flatMap(([k, v]) => flattenCountNode(k, v as CountNode))
}

/** Newest first by `started` (ISO string compare). Non-mutating. */
export function sortRunsByStarted(
  runs: FimFusionRunEntry[]
): FimFusionRunEntry[] {
  return [...runs].sort((a, b) =>
    (b.data.started ?? '').localeCompare(a.data.started ?? '')
  )
}

/**
 * Split run history into the two selectors the UI needs:
 * - `latestAttempt`: newest run of ANY status — drives the kickoff / live-status
 *   UX (a just-started run is `running` with no counts yet).
 * - `publishedRun`: newest `done` run — the coherent projection every
 *   subject/output/review view scopes to. An in-flight run has no subjects yet,
 *   so scoping projections to it would query assets that don't exist.
 */
export function selectFusionRuns(runs: FimFusionRunEntry[]): {
  latestAttempt?: FimFusionRunEntry
  publishedRun?: FimFusionRunEntry
} {
  const sorted = sortRunsByStarted(runs)
  return {
    latestAttempt: sorted[0],
    publishedRun: sorted.find((r) => r.data.status === 'done'),
  }
}
